import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { TRequestStatus } from "@models/msc.model";
import usersService from "@/services/users.service";
import { TUpdateUser, TUser } from "@/models/user.model";

type UsersState = {
  initialized: boolean;
  status: TRequestStatus;
  users: TUser[];
};

type UsersActions = {
  getAllUsers: () => Promise<TUser[]>;
  updateUser: (user: TUpdateUser) => Promise<unknown>;
  deleteUser: (userId: number, reassignToUserId?: number) => Promise<unknown>;
  inviteUser: (user: { email: string; roles: number[] }) => Promise<TUser>;
  /** Local-only role sync — used by the roles store after PUT /roles/:id/users. */
  userRolesUpdated: (payload: { id: number; roles: number[] }) => void;
  /** Local-only team sync — used by the teams store after PATCH /users/team/:id. */
  userTeamsUpdated: (payload: { id: number; teams: number[] }) => void;
};

export const useUsersStore = create<UsersState & UsersActions>()(
  devtools(
    (set, get) => ({
      initialized: false,
      status: "IDLE",
      users: [],

      getAllUsers: async () => {
        const { users, initialized } = get();
        if (initialized) return users;

        set({ status: "PENDING" }, false, "getAllUsers/pending");
        const response = await usersService.getAllUsers();
        set(
          { users: response, status: "IDLE", initialized: true },
          false,
          "getAllUsers/fulfilled"
        );
        return response;
      },

      updateUser: async (user) => {
        const response = await usersService.updateUser(user);
        set(
          (state) => ({
            users: state.users.map((i) =>
              i.id === user.id ? { ...i, ...user, roles: user.roles } : i
            ),
          }),
          false,
          "updateUser/fulfilled"
        );
        return response;
      },

      deleteUser: async (userId, reassignToUserId) => {
        const response = await usersService.deleteuser(userId, reassignToUserId);
        set(
          (state) => ({ users: state.users.filter((i) => i.id !== userId) }),
          false,
          "deleteUser/fulfilled"
        );
        return response;
      },

      inviteUser: async (user) => {
        const response = await usersService.inviteUser(user);
        set(
          (state) => ({ users: [...state.users, response] }),
          false,
          "inviteUser/fulfilled"
        );
        return response;
      },

      userRolesUpdated: ({ id, roles }) =>
        set(
          (state) => ({
            users: state.users.map((i) => (i.id === id ? { ...i, roles } : i)),
          }),
          false,
          "userRolesUpdated"
        ),

      userTeamsUpdated: ({ id, teams }) =>
        set(
          (state) => ({
            users: state.users.map((i) => (i.id === id ? { ...i, teams } : i)),
          }),
          false,
          "userTeamsUpdated"
        ),
    }),
    { name: "users" }
  )
);
